import forge from 'node-forge';

/**
 * Converts a PKCS#8 private key to PKCS#1 format
 * @param pkcs8Key - The PKCS#8 private key in PEM format
 * @returns The PKCS#1 private key in PEM format
 */
export function convertPkcs8ToPkcs1(pkcs8Key: string): string {
  try {
    // Parse the PKCS#8 private key
    const privateKey = forge.pki.privateKeyFromPem(pkcs8Key);
    
    // Convert to PKCS#1 format
    const pkcs1Pem = forge.pki.privateKeyToPem(privateKey, 1); // 1 = PKCS#1 format
    
    return pkcs1Pem;
  } catch (error) {
    console.error('Error converting PKCS#8 to PKCS#1:', error);
    throw new Error('Failed to convert private key format');
  }
}

/**
 * Converts a PKCS#8 private key to OpenSSH format
 * @param pkcs8Key - The PKCS#8 private key in PEM format
 * @returns The OpenSSH private key in PEM format
 */
export function convertPkcs8ToOpenSSH(pkcs8Key: string): string {
  try {
    // For PKCS#8 to OpenSSH conversion, we need to modify the header/footer
    // OpenSSH format uses the same key structure as PKCS#8 but with different headers
    return pkcs8Key
      .replace('-----BEGIN PRIVATE KEY-----', '-----BEGIN OPENSSH PRIVATE KEY-----')
      .replace('-----END PRIVATE KEY-----', '-----END OPENSSH PRIVATE KEY-----');
  } catch (error) {
    console.error('Error converting PKCS#8 to OpenSSH:', error);
    throw new Error('Failed to convert private key format');
  }
}

/**
 * Validates if a private key is in PKCS#8 format
 * @param privateKey - The private key to validate
 * @returns True if the key is in PKCS#8 format
 */
export function isPkcs8Format(privateKey: string): boolean {
  return privateKey.includes('-----BEGIN PRIVATE KEY-----');
}

/**
 * Validates if a private key is in PKCS#1 format
 * @param privateKey - The private key to validate
 * @returns True if the key is in PKCS#1 format
 */
export function isPkcs1Format(privateKey: string): boolean {
  return privateKey.includes('-----BEGIN RSA PRIVATE KEY-----');
}

/**
 * Validates if a private key is in OpenSSH format
 * @param privateKey - The private key to validate
 * @returns True if the key is in OpenSSH format
 */
export function isOpenSSHFormat(privateKey: string): boolean {
  return privateKey.includes('-----BEGIN OPENSSH PRIVATE KEY-----');
}

/**
 * Gets the key format of a private key
 * @param privateKey - The private key to check
 * @returns 'pkcs8' | 'pkcs1' | 'openssh' | 'unknown'
 */
export function getKeyFormat(privateKey: string): 'pkcs8' | 'pkcs1' | 'openssh' | 'unknown' {
  if (isPkcs8Format(privateKey)) {
    return 'pkcs8';
  } else if (isPkcs1Format(privateKey)) {
    return 'pkcs1';
  } else if (isOpenSSHFormat(privateKey)) {
    return 'openssh';
  }
  return 'unknown';
} 